import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { needsAccountTypeChoice, useAuthStore } from "./state/authStore";
import { initSentry } from "./lib/sentry";

initSentry();

// In production, REACT_APP_BACKEND_URL must be set at build time (e.g. in Railway Variables).
const isProd = process.env.NODE_ENV === "production";
const hasBackendUrl = !!process.env.REACT_APP_BACKEND_URL;
if (isProd && !hasBackendUrl) {
  const root = document.getElementById("root");
  if (root) {
    root.innerHTML = [
      '<div style="font-family:system-ui;max-width:520px;margin:60px auto;padding:24px;background:#1a1a2e;color:#e2e8f0;border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,0.3);">',
      '<h1 style="color:#f87171;margin:0 0 12px;">Configuration missing</h1>',
      '<p style="margin:0 0 8px;">REACT_APP_BACKEND_URL is not set for this build.</p>',
      '<p style="margin:0;font-size:14px;opacity:0.9;">In Railway: open the frontend service → Variables → add <code style="background:#334155;padding:2px 6px;border-radius:4px;">REACT_APP_BACKEND_URL</code> = your backend URL (e.g. https://kubair-xxx.up.railway.app), then redeploy.</p>',
      '</div>',
    ].join("");
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("React Error Boundary:", error, info); }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 text-center">
          <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
          <p className="mt-2 max-w-md text-sm text-muted-foreground">
            An unexpected error occurred. You can try again or return home.
          </p>
          <button
            type="button"
            className="mt-6 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </button>
          <a href="/public/home" className="mt-3 text-sm text-primary underline">Go to home</a>
          {process.env.NODE_ENV !== 'production' && (
            <pre className="mt-8 max-w-2xl w-full text-left text-xs text-muted-foreground whitespace-pre-wrap break-words">
              {String(this.state.error)}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

import GlobalShell from "./components/layout/GlobalShell";
import PublicLayout from "./components/layout/PublicLayout";
import LegacyRedirect from "./components/layout/LegacyRedirect";

import LoginPage from "./pages/auth/LoginPage";
import SignupPage from "./pages/auth/SignupPage";
import PhoneLoginPage from "./pages/auth/PhoneLoginPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import AuthCallbackPage from "./pages/auth/AuthCallbackPage";
import ChooseAccountTypePage from "./pages/auth/ChooseAccountTypePage";

import HomePage from "./pages/public/HomePage";
import App from "./App";
import AIScreener from "./AIScreener";
import StockScreenerPage from "./pages/public/market/StockScreenerPage";
import IndiaCompanyPage from "./pages/public/market/IndiaCompanyPage";
import PitchReportPage from "./pages/public/market/PitchReportPage";
import ReportsHubPage from "./pages/public/market/ReportsHubPage";
import YenomAIPage from "./pages/public/market/YenomAIPage";
import CompanyResearchPage from "./pages/public/market/CompanyResearchPage";
import CalendarPage from "./pages/public/market/CalendarPage";
import InsiderScreenerPage from "./pages/public/market/InsiderScreenerPage";
import SmartMoneyPage from "./pages/public/market/SmartMoneyPage";
import ComparePage from "./pages/public/market/ComparePage";
import DirectorNetworkPage from "./pages/public/market/DirectorNetworkPage";
import WatchlistPage from "./pages/public/market/WatchlistPage";
import NewsPage from "./pages/public/market/NewsPage";
import SectorHeatmapPage from "./pages/public/market/SectorHeatmapPage";
import MoneyFlowPage from "./pages/public/market/MoneyFlowPage";
import StockScoringPage from "./pages/public/market/StockScoringPage";
import EarningsAnalysisPage from "./pages/public/market/EarningsAnalysisPage";
import DebtMonitorPage from "./pages/public/market/DebtMonitorPage";
import SentimentDashboardPage from "./pages/public/market/SentimentDashboardPage";
import AlertRulesPage from "./pages/public/market/AlertRulesPage";
import TopMoversPage from "./pages/public/market/TopMoversPage";
import IdeasDashboardPage from "./pages/public/market/IdeasDashboardPage";
import ScannersPage from "./pages/public/market/ScannersPage";
import IndexTAPage from "./pages/public/market/IndexTAPage";
import PortfolioDashboardPage from "./pages/public/portfolio/PortfolioDashboardPage";
import HoldingsPage from "./pages/public/portfolio/HoldingsPage";
import PerformancePage from "./pages/public/portfolio/PerformancePage";
import NewsInsightsPage from "./pages/public/portfolio/NewsInsightsPage";
import AnalyticsPage from "./pages/public/portfolio/AnalyticsPage";
import AlertsPage from "./pages/public/portfolio/AlertsPage";
import AddHoldingPage from "./pages/public/portfolio/AddHoldingPage";
import StockDetailPage from "./pages/public/portfolio/StockDetailPage";
import BrokerIntegrationPage from "./pages/public/portfolio/BrokerIntegrationPage";

import SettingsPage from "./pages/SettingsPage";
import OrgSettingsPage from "./pages/public/OrgSettingsPage";
import ClientsPage from "./pages/public/market/ClientsPage";
import ClientDetailPage from "./pages/public/market/ClientDetailPage";
import SignalCenterPage from "./pages/public/market/SignalCenterPage";
import ClientPortalPage from "./pages/ClientPortalPage";
import ResearchBoardPage from "./pages/public/market/ResearchBoardPage";
import TeamChatPage from "./pages/public/market/TeamChatPage";
import CommitteePage from "./pages/public/market/CommitteePage";
import BookViewPage from "./pages/public/market/BookViewPage";
import CommitteeRunPage from "./pages/public/market/CommitteeRunPage";
import CommitteePortfolioPage from "./pages/public/market/CommitteePortfolioPage";
import LandingPage from "./pages/LandingPage";
import NotFoundPage from "./pages/NotFoundPage";
import StatusPage from "./pages/StatusPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import BillingPage from "./pages/BillingPage";
import HealthCheckUploadPage from "./pages/health/HealthCheckUploadPage";
import HealthCheckResultPage from "./pages/health/HealthCheckResultPage";
import OneStockPage from "./pages/onestock/OneStockPage";
import { attachAxiosAuthInterceptor } from "./lib/apiAuth";
import RequireUserType, { homeForUserType } from "./components/layout/RequireUserType";

function AuthInitializer({ children }) {
  const initialize = useAuthStore((s) => s.initialize);
  const isLoading = useAuthStore((s) => s.isLoading);
  React.useEffect(() => {
    attachAxiosAuthInterceptor();
    initialize();
  }, [initialize]);
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return children;
}

function ProtectedRoute({ children }) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (needsAccountTypeChoice(user) && location.pathname !== '/choose-account-type') {
    return <Navigate to="/choose-account-type" replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const userType = user?.userType;
  if (isAuthenticated) {
    return <Navigate to={needsAccountTypeChoice(user) ? '/choose-account-type' : homeForUserType(userType)} replace />;
  }
  return children;
}

// Role-aware landing for /public — institutional users land on clients,
// retail users land on home. Replaces the hard-coded Navigate to /public/home.
function PublicIndexRedirect() {
  const userType = useAuthStore((s) => s.user?.userType);
  return <Navigate to={homeForUserType(userType)} replace />;
}

if (hasBackendUrl || !isProd) {
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
  <React.StrictMode>
    <ErrorBoundary>
    <BrowserRouter>
      <AuthInitializer>
      <Routes>
        <Route path="/public/client-portal/:token" element={<ClientPortalPage />} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/login/phone" element={<PublicRoute><PhoneLoginPage /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/choose-account-type" element={<ProtectedRoute><ChooseAccountTypePage /></ProtectedRoute>} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* Productized SKUs — fully public marketing surfaces (no auth) */}
        <Route path="/healthcheck" element={<HealthCheckUploadPage />} />
        <Route path="/healthcheck/:token" element={<HealthCheckResultPage />} />
        <Route path="/one-stock" element={<OneStockPage />} />

        <Route element={<ProtectedRoute><GlobalShell /></ProtectedRoute>}>
          <Route path="/public" element={<PublicLayout />}>
            <Route index element={<PublicIndexRedirect />} />
            <Route path="home" element={<HomePage />} />
            <Route path="ai" element={<YenomAIPage />} />
            <Route path="analyzer" element={<App />} />
            <Route path="find-stocks" element={<AIScreener />} />
            <Route path="screener" element={<StockScreenerPage />} />
            <Route path="india-company/:symbol" element={<IndiaCompanyPage />} />
            <Route path="india-company/:symbol/pitch" element={<PitchReportPage />} />
            <Route path="reports" element={<ReportsHubPage />} />
            <Route path="research/:symbol" element={<CompanyResearchPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="insiders" element={<InsiderScreenerPage />} />
            <Route path="smart-money" element={<SmartMoneyPage />} />
            <Route path="compare" element={<ComparePage />} />
            <Route path="directors" element={<DirectorNetworkPage />} />
            <Route path="heatmap" element={<SectorHeatmapPage />} />
            <Route path="money-flow" element={<MoneyFlowPage />} />
            <Route path="stock-score" element={<StockScoringPage />} />
            <Route path="earnings" element={<EarningsAnalysisPage />} />
            <Route path="debt-monitor" element={<DebtMonitorPage />} />
            <Route path="sentiment" element={<SentimentDashboardPage />} />
            <Route path="alerts" element={<AlertRulesPage />} />
            <Route path="watchlist" element={<WatchlistPage />} />
            <Route path="news" element={<NewsPage />} />
            <Route path="top-movers" element={<TopMoversPage />} />
            <Route path="ideas" element={<IdeasDashboardPage />} />
            <Route path="scanners" element={<ScannersPage />} />
            <Route path="scanners/:slug" element={<ScannersPage />} />
            <Route path="index-ta" element={<IndexTAPage />} />
            <Route path="index-ta/:symbol" element={<IndexTAPage />} />
            <Route path="portfolio" element={<RequireUserType allow="retail"><PortfolioDashboardPage /></RequireUserType>} />
            <Route path="portfolio/holdings" element={<RequireUserType allow="retail"><HoldingsPage /></RequireUserType>} />
            <Route path="portfolio/performance" element={<RequireUserType allow="retail"><PerformancePage /></RequireUserType>} />
            <Route path="portfolio/news" element={<RequireUserType allow="retail"><NewsInsightsPage /></RequireUserType>} />
            <Route path="portfolio/analytics" element={<RequireUserType allow="retail"><AnalyticsPage /></RequireUserType>} />
            <Route path="portfolio/alerts" element={<RequireUserType allow="retail"><AlertsPage /></RequireUserType>} />
            <Route path="portfolio/add-holding" element={<RequireUserType allow="retail"><AddHoldingPage /></RequireUserType>} />
            <Route path="portfolio/stock/:symbol" element={<RequireUserType allow="retail"><StockDetailPage /></RequireUserType>} />
            <Route path="portfolio/broker" element={<RequireUserType allow="retail"><BrokerIntegrationPage /></RequireUserType>} />
            {/* Advisor-only surface */}
            <Route path="org-settings" element={<RequireUserType allow="institutional"><OrgSettingsPage /></RequireUserType>} />
            <Route path="clients" element={<RequireUserType allow="institutional"><ClientsPage /></RequireUserType>} />
            <Route path="clients/:clientId" element={<RequireUserType allow="institutional"><ClientDetailPage /></RequireUserType>} />
            <Route path="signals" element={<RequireUserType allow="institutional"><SignalCenterPage /></RequireUserType>} />
            <Route path="research-board" element={<RequireUserType allow="institutional"><ResearchBoardPage /></RequireUserType>} />
            <Route path="team-chat" element={<RequireUserType allow="institutional"><TeamChatPage /></RequireUserType>} />
            <Route path="committee" element={<RequireUserType allow="institutional"><CommitteePage /></RequireUserType>} />
            <Route path="committee/portfolio" element={<RequireUserType allow="institutional"><CommitteePortfolioPage /></RequireUserType>} />
            <Route path="committee/runs/:runId" element={<RequireUserType allow="institutional"><CommitteeRunPage /></RequireUserType>} />
            <Route path="book" element={<RequireUserType allow="institutional"><BookViewPage /></RequireUserType>} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/billing" element={<BillingPage />} />
          {/* Branded aliases — retail sees /research, institutional sees /advisor. */}
          <Route path="/research" element={<Navigate to="/public/home" replace />} />
          <Route path="/research/*" element={<Navigate to="/public/home" replace />} />
          <Route path="/advisor" element={<Navigate to="/public/clients" replace />} />
          <Route path="/advisor/*" element={<Navigate to="/public/clients" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Legacy redirects */}
        <Route path="/market/*" element={<LegacyRedirect />} />
        <Route path="/portfolio/*" element={<LegacyRedirect />} />
        <Route path="/institutional/*" element={<Navigate to="/public/home" replace />} />
        <Route path="/private/*" element={<Navigate to="/public/home" replace />} />
        <Route path="/secondaries/*" element={<Navigate to="/public/home" replace />} />
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/analyzer" element={<Navigate to="/public/analyzer" replace />} />
        <Route path="/find-stocks" element={<Navigate to="/public/find-stocks" replace />} />
      </Routes>
      </AuthInitializer>
    </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
  );
}
